import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, FormItem, Input, PhoneInput, Card, useToast, showConfirm
} from '../ui';
import type { Column } from '../ui';
import {
  PlusIcon, PencilIcon, TrashIcon, UserIcon, MagnifyingGlassIcon, PhoneIcon, EnvelopeIcon
} from '@heroicons/react/24/outline';
import { Customer } from '../config/supabase';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from '../hooks/api/useCustomers';

const CustomerManagement: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { success, error: showError } = useToast();

  // React Query hooks
  const { 
    data: customersData, 
    isLoading: loading, 
    error: customersError 
  } = useCustomers(currentPage, pageSize, searchTerm);

  const createCustomerMutation = useCreateCustomer();
  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();

  // Extract data from hooks
  const customers = customersData?.customers || [];
  const totalCount = customersData?.totalCount || 0;

  // Handle mutation errors
  React.useEffect(() => {
    if (customersError) {
      showError('Error fetching customers: ' + customersError.message);
    }
  }, [customersError, showError]);

  // Handle search with debouncing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    try {
      if (editingCustomer) {
        await updateCustomerMutation.mutateAsync({
          id: editingCustomer.id,
          ...values
        });
        success('Customer updated successfully');
      } else {
        await createCustomerMutation.mutateAsync(values as any);
        success('Customer created successfully');
      }

      handleModalClose();
    } catch (error: any) {
      showError('Error saving customer: ' + error.message);
    }
  };

  const handleDelete = async (id: string, customerName: string) => {
    try {
      await deleteCustomerMutation.mutateAsync(id);
      success('Customer deleted successfully');
    } catch (error: any) {
      showError('Error deleting customer: ' + error.message);
    }
  };

  const handleModalOpen = (customer?: Customer) => {
    setEditingCustomer(customer || null);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCustomer(null);
  };

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, record: Customer) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
              <UserIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{name}</div>
            {record.email && (
              <div className="text-sm text-gray-500 flex items-center">
                <EnvelopeIcon className="h-3 w-3 mr-1" />
                {record.email}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      render: (phone: string) => phone ? (
        <div className="text-sm text-gray-900 flex items-center">
          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
          {phone}
        </div>
      ) : (
        <span className="text-gray-400">No phone</span>
      ),
    },
    {
      key: 'address',
      title: 'Address',
      dataIndex: 'address',
      render: (address: string) => address ? (
        <div className="text-sm text-gray-900 max-w-xs truncate">
          {address}
        </div>
      ) : (
        <span className="text-gray-400">No address</span>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record: Customer) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleModalOpen(record)}
            className="text-blue-600 hover:text-blue-800"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              showConfirm({
                title: 'Delete Customer',
                content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                onConfirm: () => handleDelete(record.id, record.name),
                confirmText: 'Delete',
                type: 'danger'
              });
            }}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <UserIcon className="h-6 w-6 mr-2" />
          Customer Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage customer information, contact details, and rental history
        </p>
      </div>

      <div className="flex justify-between items-center">
        <Button
          variant="primary"
          size="lg"
          onClick={() => handleModalOpen()}
          className="bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add New Customer
        </Button>
        
        <div className="ml-auto">
          <Input
            placeholder="Search customers by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
            className="w-80"
          />
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={customers}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      <Modal
        isOpen={modalVisible}
        onClose={handleModalClose}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        size="md"
      >
        <Form
          onSubmit={handleSubmit}
          initialValues={editingCustomer || {}}
        >
          <div className="space-y-4">
            <FormItem name="name" label="Full Name" required>
              <Input
                placeholder="Enter customer's full name"
                autoFocus
              />
            </FormItem>

            <FormItem name="email" label="Email Address">
              <Input
                type="email"
                placeholder="customer@example.com"
                leftIcon={<EnvelopeIcon className="h-4 w-4" />}
              />
            </FormItem>

            <FormItem name="phone" label="Phone Number">
              <PhoneInput
                placeholder="555-123-4567"
              />
            </FormItem>

            <FormItem name="address" label="Address">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter customer's address..."
              />
            </FormItem>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingCustomer ? 'Update Customer' : 'Add Customer'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerManagement; 